// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
}                                       from 'react';
import {
    useNavigate,
    useLocation,
}                                       from 'react-router-dom';
import { ReactSVG }                     from 'react-svg';

// ===== Hooks =====

import {
    useTimeout,
}                                       from '../../hooks';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    USER_ACTION_TYPE,
}                                       from '../../enums';

// ===== Services =====

import {
    recordUserAction,
    updatePageTitle,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
} from '../../services';

// ===== Interfaces =====

import {
    IPartialUserItem,
}                                       from '../../interfaces';

// ===== Images =====

import VerascopeLogo                    from '../../images/verascope-logo-silhouette.svg';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    PAGE_SUBTITLE_TRANSITION_DURATION,
    SUPPORT_EMAIL_ADDRESS,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    ColorText,
    PrivacyHeader,
    PrivacyList,
    PrivacyListItem,
    PrivacyText,
    PrivacyTextContainer,
    Anchor,
}                                       from './styles';
import {
    PageLogo,
    PageSubtitle,
    PageTitle,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

interface Props {
    customCursorActive: boolean,
    user: IPartialUserItem | null,
    currentSessionId: string | null,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function AboutView({
    customCursorActive,
    user,
    currentSessionId,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element {
    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();

    // ===== State =====

    const [showText, setShowText] = useState<boolean>(false);
    // Stores the location key for which a view action has been recorded.
    const recordedViewPageUserActionKey = useRef<string | null>(null);

    // ===== Animation Constants =====

    const PRIVACY_TEXT_DURATION = 0;
    const HEADER_DELAY_DURATION = PAGE_SUBTITLE_TRANSITION_DURATION - 150;

    // ===== Methods =====

    const onLogoEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.logomark,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onLogoLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onLogoClick = async (e: React.MouseEvent): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickHomeButton,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.privacyPolicy,
                },
            });
        }
        onCursorLeave(e);
        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Records user action of viewing privacy page
     */
    const recordPrivacyPageUserAction = async (): Promise<void> => {
        if (user && currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewPrivacyPage,
                sessionId: currentSessionId,
            });
        }
    };

    // ===== Side Effects =====

    /**
     * Manages page title changes
     */
    useEffect(() => {
        updatePageTitle(
            'Privacy Policy',
        );
    }, []);

    /**
     * Records View Privacy Page User Action
     */
    useEffect(() => {
        if (
            !currentSessionId
            || !user
            || recordedViewPageUserActionKey.current === location.key
        ) return;
        recordPrivacyPageUserAction();
        recordedViewPageUserActionKey.current = location.key;
    }, [
        currentSessionId,
        location.key,
        user,
    ]);

    // Show text
    useEffect(() => {
        clearTimeoutShowText();
        timeoutShowText();
    }, []);

    const {
        start: timeoutShowText,
        clear: clearTimeoutShowText,
    } = useTimeout(() => {
        setShowText(true);
    }, HEADER_DELAY_DURATION);

    return (
        <Container>
            <PageLogo
                dark
                withEnterTransition
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onLogoEnter(e),
                    onMouseLeave: (e) => onLogoLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onLogoClick })}
            >
                <ReactSVG
                    src={VerascopeLogo}
                />
            </PageLogo>
            <PageTitle
                withEnterTransition
                color={theme.verascopeColor.purple200}
            >
                PRIVACY POLICY
            </PageTitle>
            <PageSubtitle
                withEnterTransition
                color={theme.verascopeColor.purple400}
            >
                Last Updated: August 19, 2025
            </PageSubtitle>
            <PrivacyTextContainer>
                <PrivacyText
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Verascope, Inc. (“
                    <ColorText color={theme.verascopeColor.purple500}>Verascope</ColorText>
                    ,” “
                    <ColorText color={theme.verascopeColor.purple500}>we</ColorText>
                    ,” “
                    <ColorText color={theme.verascopeColor.purple500}>us</ColorText>
                    ,” or “
                    <ColorText color={theme.verascopeColor.purple500}>our</ColorText>
                    ”) is committed to protecting your privacy.
                    This Privacy Policy explains how we collect, use, share, and protect personal information when
                    you visit or use our website and services (“
                    <ColorText color={theme.verascopeColor.purple500}>Services</ColorText>
                    ”). By using our Services, you agree to the
                    collection and use of information in accordance with this policy.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Information We Collect
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We collect
                    <ColorText color={theme.verascopeColor.purple500}> personal data </ColorText>
                    and
                    <ColorText color={theme.verascopeColor.purple500}> usage data </ColorText>
                    from all visitors in order to provide and improve our Services.
                    The information we collect includes:
                </PrivacyText>
                <PrivacyList>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Automatically Collected Data (Anonymous Visitors)</ColorText>
                        {': '}
                        When you visit our site (even without creating an account), we collect certain information automatically.
                        This includes your IP address, browser type, operating system and device type (e.g. detecting if you’re on a
                        smartphone or an Apple device), and general usage data about your interactions on the site (pages visited, clicks, etc.).
                        We may assign you a unique identifier (anonymous profile ID) via cookies or similar technologies to track your actions
                        on our site over time. This data helps us understand how visitors use our site and enables basic functionalities
                        like remembering your preferences.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Mailing List Information</ColorText>
                        {': '}
                        If you join our mailing list by providing your email address, we collect your email (and anyother information you
                        choose to provide, such as name). We use this to send you company-related updates, newsletters, or promotional
                        content
                        <ColorText color={theme.verascopeColor.purple500}> with your consent</ColorText>
                        . You can unsubscribe at any timeby clicking the unsubscribe link provided in each email.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Account Information (Registered Users)</ColorText>
                        {': '}
                        If you create a user profile on our site, we will collect the information you provide during registration. This may
                        include your name and email address (especially for Email/Password sign-up), or information from third-party
                        authentication providers if you sign up via Google, Facebook, GitHub, X/Twitter, etc. (for example, these providers
                        may share your name, email, and public profile info with us according to their own privacy policies). We also record
                        <ColorText color={theme.verascopeColor.purple500}> the date and time you joined</ColorText>
                        , the
                        <ColorText color={theme.verascopeColor.purple500}> authentication method </ColorText>
                        used (e.g. Google OAuth, X/Twitter OAuth, etc.), and whether you have verified your account (for email/password
                        registrations, we track if the verification link was clicked).
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Profile and Usage Data (Registered Users)</ColorText>
                        {': '}
                        For users who create an account, we maintain additional data in your profile to support our Services. This includes:
                        <PrivacyList>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                A record of your login
                                <ColorText color={theme.verascopeColor.purple500}> sessions </ColorText>
                                (dates/times of access) for security and troubleshooting.
                            </PrivacyListItem>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                Whether you have purchased access to our digital book (book access status).
                            </PrivacyListItem>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                A list of any
                                <ColorText color={theme.verascopeColor.purple500}> annotations </ColorText>
                                you have made in the digital book (including the content of those annotations such as text notes, images,
                                or voice notes you upload).
                            </PrivacyListItem>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                Whether you are subscribed to our mailing list and marketing emails (you can manage this in your user settings).
                            </PrivacyListItem>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                A list of items in your
                                <ColorText color={theme.verascopeColor.purple500}> shopping cart </ColorText>
                                or purchase wishlist.
                            </PrivacyListItem>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                Your
                                <ColorText color={theme.verascopeColor.purple500}> order </ColorText>
                                history of purchases on our site (e.g. which chapters, digital book, or merchandise you have bought).
                            </PrivacyListItem>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                Any
                                <ColorText color={theme.verascopeColor.purple500}> shipping addresses </ColorText>
                                you provide for physical merchandise orders (we store these to fulfill your orders and for your
                                convenience in future orders).
                            </PrivacyListItem>
                            <PrivacyListItem
                                isVisible={showText}
                                delay={0}
                                enterDuration={PRIVACY_TEXT_DURATION}
                            >
                                Whether you have opted to save your payment card details for future purchases (see “Payments”
                                below for how this works).
                            </PrivacyListItem>
                        </PrivacyList>
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Digital Book Usage</ColorText>
                        {': '}
                        Our site hosts a digital book. Both anonymous visitors and logged-in users can view free preview excerpts of chapters.
                        If you choose to navigate these previews, we may record which preview pages or chapters you viewed (this is usage data
                        that helps us gauge interest in the content). Full access to chapters or the entire book requires purchase and a user
                        account. If you have purchased digital content, we associate that content access with your user account. We do not
                        collect any additional personal data during reading, aside from any annotations you choose to create (which are linked
                        to your profile as noted above).
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>
                            Payment Information: We do not collect or store full payment card numbers directly on our servers.
                        </ColorText>
                        {' '}
                        All payments on our site are processed by our third-party payment processor, Stripe. When you make a purchase (such as buying
                        a chapter, the full digital book, or physical merchandise), you will provide your payment details (credit card number, etc.)
                        through Stripe’s secure checkout. Stripe may share with us limited information about the transaction for record-keeping (such
                        as a payment ID, card type, last four digits of the card, and payment status). If you choose to save your card for future
                        purchases, that information is stored securely by Stripe on their systems – we retain a reference token that allows us to
                        charge your saved card, but we do not see or store the card number or CVV on our servers. (Please review Stripe’s privacy
                        policy for details on how they handle your payment data.)
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Communications</ColorText>
                        {': '}
                        If you contact us (for example, by sending an email to our sales team to negotiate a price, or by emailing customer support),
                        we will collect the information you provide in that communication. This may include your name, email, and the contents of your
                        message. We use this information to respond to your inquiry and keep records of our communications.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Job Application Data</ColorText>
                        {': '}
                        Our website provides a page with links to forms for job applications. If you choose to apply for a job with Verascope via
                        those forms, you will be asked to provide personal information such as your name, contact details, resume/CV, employment
                        history, etc. That information will be collected and used solely for recruitment and hiring purposes. Note that these job
                        application forms may be hosted by a third-party service (for example, we might use an online form or recruitment platform).
                        We will treat any information received through the job application process in accordance with this Privacy Policy, and
                        additionally, the third-party platform may have its own privacy terms which will be disclosed on the application page.
                    </PrivacyListItem>
                </PrivacyList>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    How We Use Your Information
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We use the collected information for various
                    <ColorText color={theme.verascopeColor.purple500}> legitimate business purposes </ColorText>
                    consistent with this policy, including:
                    <PrivacyList>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Providing and Improving Services</ColorText>
                            {': '}
                            We process data to operate the website’s core functionality – for example, remembering your preferences and logged-in
                            status, enabling you to navigate the digital book previews, and providing access to purchased content. Tracking how
                            visitors use the site (via cookies, logs, and analytics) helps us improve site design, fix technical issues, and
                            enhance user experience.
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Account Management</ColorText>
                            {': '}
                            For registered users, we use your information to maintain your profile and provide the features associated with
                            your account. This includes authenticating you at login, keeping track of your purchases so you can access what you paid
                            for, and allowing you to create and view annotations. We also use account data like session logs and verification
                            status for security (to detect suspicious login activity and ensure only verified accounts can access certain features).
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Communication and Marketing</ColorText>
                            {': '}
                            We use contact information (like your email address) to communicate with you. This includes:
                            <PrivacyList>
                                <PrivacyListItem
                                    isVisible={showText}
                                    delay={0}
                                    enterDuration={PRIVACY_TEXT_DURATION}
                                >
                                    <ColorText color={theme.verascopeColor.purple400}>Email Newsletters & Updates</ColorText>
                                    {': '}
                                    If you joined our mailing list or created an account (accounts are automatically added to our mailing list),
                                    we will send you periodic emails about Verascope news, product updates, promotions, or events. These are
                                    marketing communications you can opt out of at any time. Each marketing email will include an
                                    <ColorText color={theme.verascopeColor.purple500}> unsubscribe link </ColorText>
                                    that you can click to be removed from future mailings. You may also manage your email preferences in
                                    your account settings.
                                </PrivacyListItem>
                                <PrivacyListItem
                                    isVisible={showText}
                                    delay={0}
                                    enterDuration={PRIVACY_TEXT_DURATION}
                                >
                                    <ColorText color={theme.verascopeColor.purple400}>Transactional and Service Emails</ColorText>
                                    {': '}
                                    We will send emails that are necessary for our Services, such as account verification emails, purchase receipts,
                                    password reset emails, and notifications related to your account activity. If you purchase the digital book
                                    and participate in annotations, we will send you
                                    <ColorText color={theme.verascopeColor.purple500}> weekly comment digest emails </ColorText>
                                    summarizing any new replies or comments on your annotations. (You can turn off the digest emails in your user
                                    settings if you prefer not to receive them.)
                                </PrivacyListItem>
                                <PrivacyListItem
                                    isVisible={showText}
                                    delay={0}
                                    enterDuration={PRIVACY_TEXT_DURATION}
                                >
                                    <ColorText color={theme.verascopeColor.purple400}>Customer Support</ColorText>
                                    {': '}
                                    If you contact us with questions or for support, we will use your provided information to communicate
                                    with you and resolve any issues.
                                </PrivacyListItem>
                            </PrivacyList>
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Email Open Tracking</ColorText>
                            {': '}
                            To help us understand engagement with our email communications (such as newsletters or digest emails), we utilize an
                            <ColorText color={theme.verascopeColor.purple500}> open-tracking pixel</ColorText>
                            . This is a tiny invisible image embedded in certain emails that, when the email is opened and images are loaded,
                            contacts our server (via a Firebase cloud function) and notifies us that the email was opened and at what time.
                            This technique is commonly used to measure email open rates. The tracking pixel will report the email ID and
                            that it was opened; it does not collect other information beyond what your email client automatically provides
                            (such as IP address or device type when fetching the image). We use this data to improve our email content and
                            sending schedules. If you prefer not to be tracked in this way, you can disable image loading in your email client
                            (though that may prevent you from seeing some content in the email).
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Processing Payments and Fulfillment</ColorText>
                            {': '}
                            When you make a purchase, we use the personal information provided to
                            <ColorText color={theme.verascopeColor.purple500}> process the transaction and fulfill your order.</ColorText>
                            This includes using your payment details to charge you via
                            <ColorText color={theme.verascopeColor.purple500}> Stripe </ColorText>
                            and using your name and shipping address to ship physical merchandise to you. We also generate shipping labels and
                            get shipping rate quotes through
                            <ColorText color={theme.verascopeColor.purple500}> Shippo</ColorText>
                            , a third-party shipping service provider, which means we will share the destination address (and package weight/size)
                            with Shippo to create the shipment. We use your email to send order confirmations and updates on shipping status if
                            applicable.
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Analytics and Aggregate Insights</ColorText>
                            {': '}
                            We may aggregate and anonymize the data we collect (for example, creating general statistics about how many users
                            use a certain browser or how many total annotations have been made on the site). Aggregated data does
                            <ColorText color={theme.verascopeColor.purple500}> not </ColorText>
                            identify individuals and is used to understand trends, monitor the performance of our business, and make decisions such as
                            which new features to develop.
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Enforcing Terms and Safety</ColorText>
                            {': '}
                            Information (especially usage data and user-generated content) may be used to
                            <ColorText color={theme.verascopeColor.purple500}> enforce our Terms of Use and community guidelines</ColorText>
                            . For example, we might monitor for fraudulent behavior (such as someone trying to bypass payment to access
                            content), or review user annotations that are reported by others to ensure no violations (e.g. harassment or illegal
                            content). We also may use certain data to
                            <ColorText color={theme.verascopeColor.purple500}> detect and prevent security incidents </ColorText>
                            or other malicious activity on our site.
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Recruitment</ColorText>
                            {': '}
                            If you submit a job application, we use your information to evaluate your qualifications, contact you for interviews,
                            and make hiring decisions. We retain recruitment data for a period necessary for the hiring process and any legal
                            reporting obligations, and will not use it for other purposes without your consent.
                        </PrivacyListItem>
                    </PrivacyList>
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Cookies and Tracking Technologies
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We use
                    <ColorText color={theme.verascopeColor.purple500}> cookies </ColorText>
                    (small text files stored on your device) and similar tracking technologies to provide our Services and collect data. For
                    example, when you visit our site, our system will set cookies to remember your anonymous profile ID, keep you logged in
                    (for registered users), and store preferences such as your language or interface settings. We may also use cookies or local
                    storage to keep track of items in your cart between visits.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Additionally, we may use cookies and device fingerprints to analyze site traffic and usage patterns (e.g., which pages are
                    most visited, how users navigate between pages). This helps us improve the design and content of our site.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    You can control or delete cookies through your browser settings. However, please note that if you disable cookies, some
                    features of our website (like maintaining your login session or cart contents) may not function properly.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Our site currently does not respond to “Do Not Track” signals, because there is no industry standard for DNT. However, we
                    only track user activities on our own website and do not engage in cross-site tracking for targeted advertising.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    How We Share Your Information
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We value your privacy and do not
                    <ColorText color={theme.verascopeColor.purple500}> sell your personal information</ColorText>
                    to third parties. However, we do share certain data with third parties in the following circumstances, to operate our
                    business and provide our Services:
                </PrivacyText>
                <PrivacyList>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Service Providers</ColorText>
                        {': '}
                        We use reputable third-party companies to facilitate our Services – for example:
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Email Delivery (SendGrid)</ColorText>
                            {': '}
                            We use SendGrid (a service by Twilio) to send out our email communications (newsletters, verification emails,
                            etc.). We provide SendGrid with the list of recipient email addresses and the content to send. SendGrid acts as
                            our data processor to deliver those emails on our behalf. We have agreements in place with such providers to safeguard
                            your data. (SendGrid does not use your email for any purpose except to send our communications, and they have their own
                            privacy and security commitments.)
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Payment Processing (Stripe)</ColorText>
                            {': '}
                            As noted, all payment transactions are handled through Stripe. When you make a purchase, you are essentially
                            providing your payment information directly to Stripe. Stripe will process the payment and send us confirmation of
                            payment. Stripe may also store your card information if you opt to save it. We share with Stripe the minimum information
                            necessary for payments (such as the transaction amount, your name/email to link the payment, and your shipping address
                            if needed for verification). Stripe is a PCI-DSS compliant payment processor and is responsible for safeguarding your
                            payment data. We recommend reviewing Stripe’s Privacy Policy for more details on their practices.
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Shipping (Shippo and Carriers)</ColorText>
                            {': '}
                            If you order physical merchandise, we use Shippo’s API to purchase shipping labels from carriers
                            (e.g., USPS, FedEx, etc.). This means your name and shipping address, and the package details, will be shared with
                            Shippo and the shipping carrier to generate the label and allow tracking. Shippo may retain shipment records as
                            required for their service (e.g., for tracking and reporting). They do not use your information for other purposes
                            beyond facilitating shipments. The carrier (postal service or courier) will of course have your address in order to
                            deliver the package.
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Authentication Providers</ColorText>
                            {': '}
                            If you choose a social login option (Google, Facebook, GitHub, X/Twitter), you will be redirected to that provider
                            to authenticate. The third-party provider will confirm your identity to us by sharing certain info (such as your
                            email and a verification token). We do not get your password for those services. These providers may record the
                            fact that you used their login to access Verascope. Your use of their authentication is also governed by their
                            privacy policies (e.g., Google’s Privacy Policy when using Google Sign-In).
                        </PrivacyListItem>
                        <PrivacyListItem
                            isVisible={showText}
                            delay={0}
                            enterDuration={PRIVACY_TEXT_DURATION}
                        >
                            <ColorText color={theme.verascopeColor.purple400}>Cloud Hosting and Functions</ColorText>
                            {': '}
                            Our website and backend may be hosted on third-party infrastructure (for example, if we use Firebase/Google Cloud
                            for certain functions like the email tracking pixel, or cloud databases to store data). These cloud service providers
                            may process stored data as part of hosting, but they are not allowed to access or use it for any other purposes.
                            We ensure any cloud providers implement strong security measures.
                        </PrivacyListItem>
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Business Transfers</ColorText>
                        {': '}
                        If Verascope, Inc. is involved in a merger, acquisition, financing due diligence, reorganization, bankruptcy, receivership,
                        sale of company assets, or transition of service to another provider, your information
                        <ColorText color={theme.verascopeColor.purple500}> may be transferred as part of that transaction</ColorText>
                        . In such cases, we would ensure the successor entity honors the commitments we have made in this Privacy Policy regarding
                        your personal data.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Legal Obligations and Safety</ColorText>
                        {': '}
                        We may disclose personal information if required to do so by law or in the good-faith belief that such action is
                        necessary to (a) comply with a legal obligation, such as a subpoena, court order, or regulatory demand; (b) protect
                        and defend the rights or property of Verascope, Inc.; (c) act in urgent circumstances to protect the personal safety of
                        users of the Services or the public; or (d) protect against legal liability. This includes sharing information with law
                        enforcement or government agencies when required by applicable laws.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>With Your Consent</ColorText>
                        {': '}
                        We will share your personal data with other third parties for purposes not covered by this Privacy Policy only with
                        your explicit consent. For example, if in the future we wish to feature a user testimonial with your name or share your
                        info with a partner company for their marketing, we would ask for your consent first (this is just an illustrative example;
                        we have no such data sharing arrangements at this time).
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Public Content</ColorText>
                        {': '}
                        Certain information you provide might be visible to others on the website by nature of the Service. Specifically,
                        <ColorText color={theme.verascopeColor.purple500}> annotations </ColorText>
                        you create on the digital book can be viewed by other users who have purchased access to the same book content (and
                        vice versa, you can see their annotations). If your user profile includes a display name or username, that name will
                        be shown alongside your annotations.
                        <ColorText color={theme.verascopeColor.purple500}> Please be mindful </ColorText>
                        that any content you contribute in annotations, including text or images you upload in notes, can be read by others. Do
                        not include sensitive personal information in annotations or any public forums on our site. We are not responsible for
                        other users’ use of the information you publicly post.
                    </PrivacyListItem>
                </PrivacyList>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We do
                    <ColorText color={theme.verascopeColor.purple500}> not </ColorText>
                    sell personal data to advertisers or third-party marketers. We also do not share your personal information with third parties
                    for their own direct marketing purposes unless you have given us permission.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Data Security
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We take the security of your personal information seriously. We implement
                    <ColorText color={theme.verascopeColor.purple500}> appropriate technical and organizational measures </ColorText>
                    to protect your data against unauthorized access, alteration, disclosure, or destruction. This includes encryption of
                    data in transit (our website is served over HTTPS/TLS) and encryption of sensitive data at rest where applicable. Passwords
                    for user accounts are stored in hashed form (we never store plain text passwords). We restrict access to personal data to
                    our employees or contractors who need to process it and who are subject to strict confidentiality obligations.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to
                    use commercially acceptable means to protect your personal information, we
                    <ColorText color={theme.verascopeColor.purple500}> cannot guarantee absolute security </ColorText>
                    . You should also take steps to secure your account – for example, choose a strong password and do not share it with
                    others. If you suspect any unauthorized access to your account, please notify us immediately so we can help secure your account.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Data Retention
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy,
                    <ColorText color={theme.verascopeColor.purple500}> unless a longer retention period is required or permitted by law</ColorText>
                    . For example, if you create an account, we will retain your profile information and content for as long as your account is
                    active or as needed to provide you with services. If you delete your account (or request deletion), we will remove or anonymize
                    personal data associated with your account, except for information we are required to keep for legal or compliance reasons
                    (such as transaction records for accounting/tax, or logs needed for security and fraud prevention).
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Even if you are not a registered user, information collected via cookies and other tracking may be stored for a certain period.
                    Browser analytics data may be retained for a shorter period (e.g., we might rotate logs every 12-18 months). Email subscription
                    data is retained until you unsubscribe, at which point we will stop sending you emails, but may keep a record of your request
                    to ensure we honor it going forward.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Job application information may be retained for a period after the hiring process (typically in line with equal opportunity and
                    record-keeping laws, e.g., one year) and then deleted or archived securely.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    When we no longer have a legitimate need to process your data, we will delete, anonymize, or securely store your information and
                    isolate it from further processing until deletion is possible.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Your Rights and Choices
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Depending on your jurisdiction, you may have certain rights regarding your personal information. We strive to provide all users
                    with appropriate control over their data:
                </PrivacyText>
                <PrivacyList>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Access and Correction</ColorText>
                        {': '}
                        ou may access and update much of your account information by logging into your account and editing your profile or settings.
                        For any personal information not editable through the platform (or if you are not a registered user and wish to inquire what
                        data we have about you), you can contact us to request access to the information we hold about you. We will provide it to you,
                        subject to verification of your identity and as required by law. If you find any inaccuracies in your personal data, please
                        let us know so we can correct it.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Deletion and Account Closure</ColorText>
                        {': '}
                        If you no longer want to use our Services, you have the option to delete your account in your user settings or by contacting
                        us. Deleting your account will remove your profile information from our active systems. Note that content you posted (such as
                        annotations) may remain visible to other users even after your account is deleted, but they will be dissociated from your
                        identity (for example, past annotations might be labeled as from a “Deleted User”). We will also retain certain transaction
                        records as required for business and legal purposes. If you are not a registered user but wish to have us delete personal
                        data we may have (like an email on our mailing list or an IP address in logs that is identifiable to you), please contact
                        us with the request. We will honor deletion or “forget me” requests as required by applicable laws like GDPR or CCPA.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Opt-Out of Marketing</ColorText>
                        {': '}
                        As noted, you can unsubscribe from marketing emails at any time via the unsubscribe link or by adjusting your account
                        preferences. Even after you opt out of marketing, we may still send you essential service or transaction-related emails
                        (e.g., purchase receipts or account alerts) because those are not promotional in nature.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Cookie Choices</ColorText>
                        {': '}
                        You can manage cookie preferences through your browser. If we implement a cookie consent banner (depending on legal
                        requirements in your region), you can choose which categories of cookies to allow. Currently, we only use cookies for
                        strictly necessary and functional purposes, and not for third-party advertising.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Do Not Sell (California/Virginia/etc.)</ColorText>
                        {': '}
                        We do not sell personal information, so there is no need to opt out of sale. If we ever consider using your data in a way
                        that qualifies as a “sale” under privacy laws, we will provide a mechanism for you to opt out.
                    </PrivacyListItem>
                    <PrivacyListItem
                        isVisible={showText}
                        delay={0}
                        enterDuration={PRIVACY_TEXT_DURATION}
                    >
                        <ColorText color={theme.verascopeColor.purple400}>Non-Discrimination</ColorText>
                        {': '}
                        We will not discriminate against you for exercising any privacy rights. For example, if you request deletion of your data,
                        we will not deny you services except insofar as we require data to provide the service (for instance, if you
                        delete your account data, you will no longer be able to log in – that’s a natural consequence, not discrimination).
                    </PrivacyListItem>
                </PrivacyList>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    If you wish to exercise any rights or have questions about your personal data, you can contact us (see Contact Us section
                    below). We may need to verify your identity (for example, by asking you to confirm control of an email address or requesting
                    additional info) before fulfilling certain requests, especially for access or deletion of data, to ensure we are protecting
                    the right person’s privacy.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Children’s Privacy
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We understand the importance of protecting children’s privacy. Our website and content (the book and other materials)
                    are generally suitable for a general audience and
                    <ColorText color={theme.verascopeColor.purple500}> not of an inappropriate nature</ColorText>
                    , so we have
                    <ColorText color={theme.verascopeColor.purple500}> no strict age restrictions on who may view the site</ColorText>
                    .
                    <ColorText color={theme.verascopeColor.purple500}> However, our Services are not directed to children under 13 years of age</ColorText>
                    , and we do not knowingly collect personal information from children under 13 without parental consent. If you are under 13,
                    <ColorText color={theme.verascopeColor.purple500}> please do not create an account or submit personal information </ColorText>
                    (such as your name or email address) on the site without your parent or guardian’s
                    permission. Minors under 18 may use the site to browse content, but if you wish to register or make purchases, we advise
                    that you do so with the involvement of a parent or guardian, since entering into transactions may require an adult’s consent.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    If we learn that we have inadvertently collected personal data from a child under 13 (or under 16 in certain jurisdictions)
                    without proper consent, we will take steps to delete that information as soon as possible.
                    <ColorText color={theme.verascopeColor.purple500}> Parents or guardians</ColorText>
                    : If you believe your child under 13 has provided personal information to us, please contact us immediately. We will
                    promptly remove the information and terminate the child’s account if applicable, in accordance with the Children’s Online
                    Privacy Protection Act (COPPA) and other relevant laws.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Changes to This Privacy Policy
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements,
                    or other factors. When we update the policy, we will change the “Last Updated” date at the top of this page. If the changes
                    are significant, we may also provide a more prominent notice (such as a banner on our website or an email notification). We
                    encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Your continued use of our Services after any changes to this Privacy Policy constitutes your acceptance of the revised policy.
                    If you do not agree with the changes, you should stop using the Services and, if applicable, delete your account or opt out
                    of the features affected by the changes.
                </PrivacyText>
                <PrivacyHeader
                    marginTop
                    color={theme.verascopeColor.purple400}
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    Contact Us
                </PrivacyHeader>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us at:
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    <ColorText color={theme.verascopeColor.purple500}>Verascope, Inc. </ColorText>
                    (d/b/a “verascope”)
                    <br />
                    Email:
                    {' '}
                    <Anchor
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
                        target="_blank"
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onCursorEnter(
                                CURSOR_TARGET.email,
                                [CURSOR_SIGN.click],
                                e.currentTarget as HTMLElement,
                            ),
                            onMouseLeave: (e) => onCursorLeave(e),
                        })}
                    >
                        {SUPPORT_EMAIL_ADDRESS}
                    </Anchor>
                    {' '}
                    (for privacy inquiries)
                </PrivacyText>
                <PrivacyText
                    marginTop
                    isVisible={showText}
                    delay={0}
                    enterDuration={PRIVACY_TEXT_DURATION}
                >
                    We will do our best to address your inquiry promptly and thoroughly. Your privacy is important to us, and we welcome your
                    feedback.
                </PrivacyText>
            </PrivacyTextContainer>
        </Container>
    );
}

export default AboutView;
